import { useCallback, useEffect, useMemo, useState } from "react";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import {
  fetchSupabaseCollection,
  loadCachedValue,
  replaceSupabaseCollection,
  saveCachedValue,
  subscribeToCachedValue,
} from "@/integrations/supabase/contentStore";

const STORAGE_KEY = "afiliabr_admin_marketplaces";

export type MarketplaceEntryType = "marketplace" | "official_store";

export type AdminMarketplace = {
  id: number;
  name: string;
  slug: string;
  website: string;
  active: boolean;
  order: number;
  entryType: MarketplaceEntryType;
};

type MarketplaceRow = {
  id: number | string;
  name: string;
  slug: string;
  website: string;
  active: boolean;
  display_order: number | string;
  entry_type?: string | null;
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function normalizeEntryType(value?: string | null): MarketplaceEntryType {
  return value === "official_store" ? "official_store" : "marketplace";
}

function normalizeMarketplace(marketplace: Partial<AdminMarketplace>, index = 0): AdminMarketplace {
  const name = (marketplace.name ?? "").trim();

  return {
    id: Number(marketplace.id ?? 0),
    name,
    slug: marketplace.slug?.trim() || slugify(name),
    website: marketplace.website?.trim() ?? "",
    active: Boolean(marketplace.active),
    order: Number(marketplace.order ?? index),
    entryType: normalizeEntryType(marketplace.entryType),
  };
}

function normalizeMarketplaces(data: Partial<AdminMarketplace>[]) {
  return data
    .map((marketplace, index) => normalizeMarketplace(marketplace, index))
    .sort((first, second) => first.order - second.order)
    .map((marketplace, index) => ({ ...marketplace, order: index }));
}

function mapRow(row: MarketplaceRow): AdminMarketplace {
  return normalizeMarketplace({
    id: Number(row.id),
    name: row.name,
    slug: row.slug,
    website: row.website,
    active: row.active,
    order: Number(row.display_order ?? 0),
    entryType: normalizeEntryType(row.entry_type),
  });
}

function mapToRow(marketplace: AdminMarketplace) {
  return {
    id: marketplace.id,
    name: marketplace.name,
    slug: marketplace.slug,
    website: marketplace.website,
    active: marketplace.active,
    display_order: marketplace.order,
    entry_type: marketplace.entryType,
  };
}

function load() {
  return normalizeMarketplaces(loadCachedValue<AdminMarketplace[]>(STORAGE_KEY, []));
}

function save(data: AdminMarketplace[]) {
  saveCachedValue(STORAGE_KEY, normalizeMarketplaces(data));
}

async function fetchFromSupabase() {
  const rows = await fetchSupabaseCollection<MarketplaceRow>("marketplaces", "display_order");
  return rows.map((row) => mapRow(row));
}

async function syncToSupabase(marketplaces: AdminMarketplace[]) {
  const rows = await replaceSupabaseCollection({
    table: "marketplaces",
    rows: normalizeMarketplaces(marketplaces).map((marketplace) => mapToRow(marketplace)),
    orderBy: "display_order",
  });

  return (rows as MarketplaceRow[]).map((row) => mapRow(row));
}

export function getStoredAdminMarketplaces() {
  return load();
}

export function getMarketplaceOptions() {
  return getStoredAdminMarketplaces()
    .filter((marketplace) => marketplace.active && marketplace.entryType === "marketplace")
    .map((marketplace) => marketplace.name);
}

export function getOfficialStoreOptions() {
  return getStoredAdminMarketplaces()
    .filter((marketplace) => marketplace.active && marketplace.entryType === "official_store")
    .map((marketplace) => marketplace.name);
}

export function useAdminMarketplaces() {
  const { session } = useSessionContext();
  const [marketplaces, setMarketplaces] = useState<AdminMarketplace[]>(load);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEY, [], (value) => {
      setMarketplaces(normalizeMarketplaces(value as AdminMarketplace[]));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setMarketplaces(remote);
          save(remote);
          return;
        }

        const cached = load();
        if (!session || cached.length === 0) return;

        const seeded = await syncToSupabase(cached);
        if (!active) return;

        setMarketplaces(seeded);
        save(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const persist = useCallback((nextData: AdminMarketplace[]) => {
    const normalized = normalizeMarketplaces(nextData);
    setMarketplaces(normalized);
    save(normalized);

    void syncToSupabase(normalized).then((remote) => {
      setMarketplaces(remote);
      save(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addMarketplace = useCallback((marketplace: Omit<AdminMarketplace, "id" | "order">) => {
    const newId = Math.max(0, ...marketplaces.map((item) => item.id)) + 1;
    persist([...marketplaces, normalizeMarketplace({ ...marketplace, id: newId, order: marketplaces.length })]);
  }, [marketplaces, persist]);

  const updateMarketplace = useCallback((id: number, data: Partial<AdminMarketplace>) => {
    persist(marketplaces.map((item) => (item.id === id ? normalizeMarketplace({ ...item, ...data, id }, item.order) : item)));
  }, [marketplaces, persist]);

  const deleteMarketplace = useCallback((id: number) => {
    persist(marketplaces.filter((item) => item.id !== id));
  }, [marketplaces, persist]);

  const toggleActive = useCallback((id: number) => {
    persist(marketplaces.map((item) => (item.id === id ? { ...item, active: !item.active } : item)));
  }, [marketplaces, persist]);

  const resetToDefault = useCallback(() => {
    persist([]);
  }, [persist]);

  const activeMarketplaceOptions = useMemo(
    () => marketplaces
      .filter((marketplace) => marketplace.active && marketplace.entryType === "marketplace")
      .map((marketplace) => marketplace.name),
    [marketplaces]
  );

  const activeOfficialStoreOptions = useMemo(
    () => marketplaces
      .filter((marketplace) => marketplace.active && marketplace.entryType === "official_store")
      .map((marketplace) => marketplace.name),
    [marketplaces]
  );

  return {
    marketplaces,
    activeMarketplaceOptions,
    activeOfficialStoreOptions,
    addMarketplace,
    updateMarketplace,
    deleteMarketplace,
    toggleActive,
    resetToDefault,
    slugify,
  };
}
